import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { Estate } from "../../../models/Estate";
import { OUTCOMES } from "../../../constants";
import {
  MultiSelectButtonForm,
  SelectButton,
} from "../shared/MultiSelectButtonForm";
import {
  BankAccountIcon,
  ChangeOfAddressIcon,
  DeathCertIcon,
  EstateEINIcon,
  FreezeCreditIcon,
  FuneralFlowersIcon,
  JustStartedIcon,
  NoticeToCreditorsIcon,
  ObituaryIcon,
  ProbateCourtIcon,
  SocialSecurityIcon,
} from "../shared/LineIcons";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  deathCert = "Ordered death certificates",
  ein = "Filed for an estate EIN",
  noticeToCreditors = "Submitted Notice to Creditors",
  estateBankAccount = "Opened a bank account for the estate’s expenses",
  funeral = "Already had the funeral",
  obituary = "Posted public obituary online or in a newspaper",
  locatedProbateCourt = "Located the correct probate court",
  stoppedSSI = "Stopped social security checks",
  frozeCredit = "Froze deceased credit", // TODO: inject deceased name - plural possessive
  changeOfEstateAddress = "Filed for change of estate address",
  nothing = "Nothing—I just started the process",
}

const iconStyles = { width: 60, height: 60 } as const;
const iconViewBox = "0 0 60 60";

const icons: { [key: string]: JSX.Element } = {
  deathCert: <DeathCertIcon sx={iconStyles} viewBox={iconViewBox} />,
  ein: <EstateEINIcon sx={iconStyles} viewBox={iconViewBox} />,
  noticeToCreditors: (
    <NoticeToCreditorsIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  estateBankAccount: <BankAccountIcon sx={iconStyles} viewBox={iconViewBox} />,
  funeral: <FuneralFlowersIcon sx={iconStyles} viewBox={iconViewBox} />,
  obituary: <ObituaryIcon sx={iconStyles} viewBox={iconViewBox} />,
  locatedProbateCourt: (
    <ProbateCourtIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  stoppedSSI: <SocialSecurityIcon sx={iconStyles} viewBox={iconViewBox} />,
  frozeCredit: <FreezeCreditIcon sx={iconStyles} viewBox={iconViewBox} />,
  changeOfEstateAddress: (
    <ChangeOfAddressIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  nothing: <JustStartedIcon sx={iconStyles} viewBox={iconViewBox} />,
};

const outcomesDictionary = enumToStringDictionary(Outcomes);
const doneSelectButtons: SelectButton[] = [];
Object.keys(outcomesDictionary).forEach((key: string) =>
  doneSelectButtons.push({
    key,
    text: outcomesDictionary[key],
    icon: icons[key],
  })
);

export const DoneSoFarScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, setActiveEstate } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (checked: string[]): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    // const estate = activeEstate ? Estate.from(activeEstate) : new Estate();
    const estate = activeEstate || new Estate();
    // console.log("checked", checked);
    estate.tasksCompleted = checked;

    try {
      await estate.saveEstate();
      setActiveEstate(estate);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper
      title="Help us understand what you’ve done so far and we’ll craft a personalized to-do list for you."
      subTitle="Select any of the following that apply."
    >
      <MultiSelectButtonForm
        initChecked={activeEstate?.tasksCompleted || undefined}
        selectButtons={doneSelectButtons}
        isOneRequired={true}
        noneOfAboveButtonKey="nothing"
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
